import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  FileText,
  ListOrdered,
  Loader2,
  Paperclip,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import { Card } from '@/components/atoms/card';
import { lineIdentityKey } from '@/lib/sales/lineIdentity';
import { calcLineSubtotal } from '@/lib/sales/pricingMode';
import { proposalTemplateLineItemToDto } from '@/lib/sales/proposal-template-line-items';
import { fmt, formatDate } from '@/components/organisms/estimates/support/estimate-ui-helpers';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Textarea } from '@/components/atoms/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/atoms/tabs';
import { useProposalTemplate } from '@/hooks/proposalTemplates/useProposalTemplate';
import { useUpdateProposalTemplate } from '@/hooks/proposalTemplates/useUpdateProposalTemplate';
import { FormPageLayout, DetailPageLayout } from '@/components/layouts';
import { CollapsibleSectionHeader, DetailCard, FormAccordionCard } from '@/components/molecules';
import { TermsSelector } from '@/components/organisms/TermsSelector';
import { TemplateAttachmentsSection } from '@/components/organisms/templates/TemplateAttachmentsSection';
import { TemplateDetailsSection } from '@/components/organisms/templates/TemplateDetailsSection';
import { soleBusinessUnitId } from '@/lib/business-units/businessUnitScope';
import { TemplateEditorRouteBoundary } from '@/components/organisms/templates/TemplateEditorRouteBoundary';
import { TemplateUnsavedChangesDialog } from '@/components/organisms/templates/TemplateUnsavedChangesDialog';
import { ProposalOptionSummaryList } from '@/components/organisms/proposals/ProposalOptionSummaryList';
import {
  SalesDocumentDetailsCard,
  SalesDocumentNotesDocsSection,
} from '@/components/organisms/sales/SalesDocumentDetailSections';
import { TemplateActions } from '@/components/organisms/templates/TemplateActions';
import { TemplateMissingPanel } from '@/components/organisms/templates/TemplateMissingPanel';
import { useProposalTemplateAttachments } from '@/hooks/proposalTemplates/useProposalTemplateAttachments';
import type { UpdateProposalTemplateDto, ProposalTemplateOption } from '@/types/proposalTemplate';
import { TIER_CONFIG, type EditableLineItem } from '@/components/organisms/OptionCard';
import {
  ProposalTemplateOptionsSection,
  ProposalTemplateEstimateTemplatePicker,
  createProposalTemplateOptionDraft,
  calcProposalTemplateOptionTotal,
  type ProposalTemplateOptionDraft,
} from '@/components/organisms/proposals/ProposalTemplateOptionsSection';
import { mapProposalLineItemToEditable } from '@/routes/_dashboard/dashboard/proposals/_shared';
import { useTemplateEditNavigationGuard } from '@/hooks/useTemplateEditNavigationGuard';
import {
  EMPTY_TERMS_SELECTION,
  readTermsSelection,
  toTermsPayload,
  type TermsSelection,
} from '@/lib/terms';

const PROPOSAL_TEMPLATE_TABS = ['overview', 'options', 'notes'] as const;
type ProposalTemplateTab = (typeof PROPOSAL_TEMPLATE_TABS)[number];

function proposalTemplateEditSnapshot({
  name,
  description,
  notes,
  terms,
  options,
  businessUnitIds,
}: {
  name: string;
  description: string;
  notes: string;
  terms: TermsSelection;
  options: ProposalTemplateOptionDraft[];
  businessUnitIds: string[];
}) {
  return JSON.stringify({
    name,
    description,
    notes,
    terms,
    businessUnitIds,
    options: options.map((option) => ({
      label: option.label,
      description: option.description,
      color: option.color,
      estimateTemplateId: option.estimateTemplateId ?? null,
      lineItems: option.lineItems.map(proposalTemplateLineItemToDto),
    })),
  });
}

export const Route = createFileRoute('/_dashboard/dashboard/settings/templates/proposal/$id')({
  validateSearch: (search): { tab: ProposalTemplateTab; mode?: 'edit' } => ({
    tab: PROPOSAL_TEMPLATE_TABS.includes(search.tab as ProposalTemplateTab)
      ? (search.tab as ProposalTemplateTab)
      : 'overview',
    ...(search.mode === 'edit' ? { mode: 'edit' as const } : {}),
  }),
  component: ProposalTemplateRoutePage,
});

function ProposalTemplateRoutePage() {
  const { id } = Route.useParams();

  return (
    <TemplateEditorRouteBoundary templateId={id}>
      <EditProposalTemplatePage />
    </TemplateEditorRouteBoundary>
  );
}

function EditProposalTemplatePage() {
  const router = useRouter();
  const navigate = Route.useNavigate();
  const { id } = Route.useParams();
  const { tab, mode } = Route.useSearch();
  const isEditing = mode === 'edit';
  const enterEdit = () =>
    void navigate({ search: (prev) => ({ ...prev, mode: 'edit' as const }), replace: true });
  const exitEdit = () =>
    void navigate({
      search: (prev) => {
        const { mode: _m, ...rest } = prev;
        return rest;
      },
      replace: true,
    });
  const { data: template, isLoading, isError, error } = useProposalTemplate(id);
  const updateMutation = useUpdateProposalTemplate();

  const [showSkeleton, setShowSkeleton] = useState(false);
  useEffect(() => {
    if (!isLoading) return;
    const t = setTimeout(() => setShowSkeleton(true), 150);
    return () => clearTimeout(t);
  }, [isLoading]);
  const { data: attachments = [] } = useProposalTemplateAttachments(id);

  const [sec, setSec] = useState({
    details: true,
    options: true,
    notes: false,
    attachments: false,
  });
  const toggleSec = (k: keyof typeof sec) => setSec((p) => ({ ...p, [k]: !p[k] }));
  const [viewCollapsed, setViewCollapsed] = useState<Set<string>>(new Set());
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState<TermsSelection>(EMPTY_TERMS_SELECTION);
  const [options, setOptions] = useState<ProposalTemplateOptionDraft[]>([]);
  // EMPTY = offered to every business unit (a wildcard), not "none".
  const [businessUnitIds, setBusinessUnitIds] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);
  const initialEditSnapshotRef = useRef<string | null>(null);

  function toggleViewCollapse(key: string) {
    setViewCollapsed((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  useEffect(() => {
    if (!template || initialized) return;
    const nextName = template.name ?? '';
    const nextDescription = template.description ?? '';
    const nextNotes = template.notes ?? '';
    const nextTerms = readTermsSelection({
      termsMode: template.termsMode,
      termsTemplateIds: template.termsTemplateIds,
      termsAndConditions: template.terms,
    });

    const rawOptions = template.options ?? [];
    const drafts = rawOptions.map((opt, idx) => {
      const items = opt.lineItems ?? [];
      return createProposalTemplateOptionDraft({
        label: opt.label ?? '',
        description: opt.description ?? '',
        color: opt.color ?? TIER_CONFIG[idx % TIER_CONFIG.length].accent,
        estimateTemplateId: opt.estimateTemplateId ?? undefined,
        lineItems: items.map((li) =>
          mapProposalLineItemToEditable(li as unknown as Record<string, unknown>),
        ),
        collapsed: false,
      });
    });
    const nextBusinessUnitIds = template.businessUnitIds ?? [];
    setName(nextName);
    setDescription(nextDescription);
    setNotes(nextNotes);
    setTerms(nextTerms);
    setOptions(drafts);
    setBusinessUnitIds(nextBusinessUnitIds);
    setViewCollapsed(new Set(drafts.map((draft) => draft.key)));
    initialEditSnapshotRef.current = proposalTemplateEditSnapshot({
      name: nextName,
      description: nextDescription,
      notes: nextNotes,
      terms: nextTerms,
      options: drafts,
      businessUnitIds: nextBusinessUnitIds,
    });
    setInitialized(true);
  }, [template, initialized]);

  function resetForm() {
    if (!template) return;
    setName(template.name ?? '');
    setDescription(template.description ?? '');
    setNotes(template.notes ?? '');
    setTerms(
      readTermsSelection({
        termsMode: template.termsMode,
        termsTemplateIds: template.termsTemplateIds,
        termsAndConditions: template.terms,
      }),
    );
    const rawOptions = template.options ?? [];
    setOptions(
      rawOptions.map((opt, idx) => ({
        ...createProposalTemplateOptionDraft({
          label: opt.label ?? '',
          description: opt.description ?? '',
          color: opt.color ?? TIER_CONFIG[idx % TIER_CONFIG.length].accent,
          estimateTemplateId: opt.estimateTemplateId ?? undefined,
          lineItems: (opt.lineItems ?? []).map((li) =>
            mapProposalLineItemToEditable(li as unknown as Record<string, unknown>),
          ),
          collapsed: false,
        }),
      })),
    );
    setBusinessUnitIds(template.businessUnitIds ?? []);
  }

  const currentEditSnapshot = useMemo(
    () =>
      proposalTemplateEditSnapshot({ name, description, notes, terms, options, businessUnitIds }),
    [name, description, notes, terms, options, businessUnitIds],
  );
  const hasUnsavedWork =
    isEditing &&
    initialized &&
    !updateMutation.isPending &&
    initialEditSnapshotRef.current !== currentEditSnapshot;
  const { blocker, allowNextNavigation } = useTemplateEditNavigationGuard(hasUnsavedWork);

  function discardAndExit() {
    allowNextNavigation();
    resetForm();
    exitEdit();
  }

  const addOption = useCallback(() => {
    setOptions((prev) => [...prev, createProposalTemplateOptionDraft()]);
  }, []);

  const updateOption = useCallback(
    (key: string, updates: Partial<Omit<ProposalTemplateOptionDraft, 'key'>>) => {
      setOptions((prev) => prev.map((o) => (o.key === key ? { ...o, ...updates } : o)));
    },
    [],
  );

  const removeOption = useCallback((key: string) => {
    setOptions((prev) => prev.filter((o) => o.key !== key));
  }, []);

  const importFromEstimateTemplate = useCallback(
    (
      optKey: string,
      template: {
        id?: string;
        name: string;
        lineItems?: Record<string, unknown>[];
      },
    ) => {
      const rawItems = template.lineItems ?? [];
      const imported: EditableLineItem[] = rawItems.map((li) =>
        mapProposalLineItemToEditable(li as unknown as Record<string, unknown>),
      );

      setOptions((prev) =>
        prev.map((o) => {
          if (o.key !== optKey) return o;
          const existingIds = new Set(o.lineItems.map((li) => li.lineItemId));
          const newItems = imported.filter((li) => !existingIds.has(li.lineItemId));
          return {
            ...o,
            estimateTemplateId: template.id,
            lineItems: [...o.lineItems, ...newItems],
          };
        }),
      );
      toast.success(`Imported ${imported.length} items from ${template.name}`);
    },
    [],
  );

  const updateLineItem = useCallback(
    // `identity` is `lineIdentityKey(line)`: two configurations of one
    // configurable item share a catalog id, so matching on that edits both rows.
    (optKey: string, identity: string, updates: Partial<EditableLineItem>) => {
      setOptions((prev) =>
        prev.map((o) => {
          if (o.key !== optKey) return o;
          return {
            ...o,
            lineItems: o.lineItems.map((li) => {
              if (lineIdentityKey(li) !== identity) return li;
              const next = { ...li, ...updates };
              if (updates.isOptional === false) next.isSelected = true;
              return next;
            }),
          };
        }),
      );
    },
    [],
  );

  const removeLineItem = useCallback((optKey: string, identity: string) => {
    setOptions((prev) =>
      prev.map((o) => {
        if (o.key !== optKey) return o;
        return { ...o, lineItems: o.lineItems.filter((li) => lineIdentityKey(li) !== identity) };
      }),
    );
  }, []);

  const reorderLineItems = useCallback((optKey: string, oldIndex: number, newIndex: number) => {
    setOptions((prev) =>
      prev.map((o) => {
        if (o.key !== optKey) return o;
        const next = [...o.lineItems];
        const [removed] = next.splice(oldIndex, 1);
        next.splice(newIndex, 0, removed);
        return { ...o, lineItems: next };
      }),
    );
  }, []);

  const replaceLineItems = useCallback((optKey: string, lineItems: EditableLineItem[]) => {
    setOptions((prev) =>
      prev.map((option) => (option.key === optKey ? { ...option, lineItems } : option)),
    );
  }, []);

  const handleSave = () => {
    if (!name.trim()) {
      toast.error('Template name is required');
      return;
    }
    if (options.length === 0) {
      toast.error('Add at least one option');
      return;
    }
    for (const opt of options) {
      if (!opt.label.trim()) {
        toast.error('Every option needs a label');
        return;
      }
      if (opt.lineItems.length === 0) {
        toast.error(`Option "${opt.label}" needs at least one line item`);
        return;
      }
      if (opt.lineItems.some((li) => !li.code && !li.name.trim())) {
        toast.error(`Name each custom item in "${opt.label}"`);
        return;
      }
    }

    const dto: UpdateProposalTemplateDto = {
      name: name.trim(),
      description: description.trim() || undefined,
      notes: notes.trim() || undefined,
      businessUnitIds,
      ...toTermsPayload(terms),
      options: options.map((o) => {
        const subtotal = o.lineItems.reduce((s, li) => s + calcLineSubtotal(li), 0);
        return {
          label: o.label.trim(),
          description: o.description.trim() || undefined,
          color: o.color,
          estimateTemplateId: o.estimateTemplateId ?? null,
          lineItems: o.lineItems.map(proposalTemplateLineItemToDto),
          subtotal,
          taxTotal: 0,
          total: subtotal,
        } as ProposalTemplateOption;
      }),
    };

    updateMutation.mutate(
      { id, dto },
      {
        onSuccess: () => {
          initialEditSnapshotRef.current = currentEditSnapshot;
          allowNextNavigation();
          exitEdit();
        },
      },
    );
  };

  if (isLoading) {
    if (!showSkeleton) return null;
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // A 404 is the only "this template is gone" signal — other failures keep the
  // normal path (and their own toast) rather than claiming a deletion.
  if (isError && error?.status === 404) {
    return <TemplateMissingPanel kind="proposal" />;
  }

  // ── View mode ─────────────────────────────────────────────────────────────
  if (!isEditing) {
    return (
      <DetailPageLayout
        key={id}
        header={
          <>
            <div className="flex items-center gap-2 min-w-0">
              <Button
                variant="ghost"
                size="icon-sm"
                className="shrink-0 md:hidden"
                onClick={() => void router.navigate({ to: '/dashboard/settings/templates' })}
              >
                <ArrowLeft size={16} />
              </Button>
              <div className="min-w-0">
                <h2 className="text-base font-semibold truncate">{name || template?.name}</h2>
                {description && (
                  <p className="text-xs text-muted-foreground truncate">{description}</p>
                )}
              </div>
            </div>
            <TemplateActions
              id={id}
              name={name || template?.name || ''}
              kind="proposal"
              variant="header"
              onEdit={() => enterEdit()}
            />
          </>
        }
      >
        <Tabs
          value={tab}
          onValueChange={(t) =>
            void navigate({
              search: (prev) => ({ ...prev, tab: t as ProposalTemplateTab }),
              replace: true,
            })
          }
        >
          <TabsList
            variant="line"
            className="w-full justify-start border-b rounded-none px-0 h-auto pb-0"
          >
            <TabsTrigger value="overview">
              <FileText className="size-3.5" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="options">
              <ListOrdered className="size-3.5" />
              Options
              {options.length > 0 && (
                <span className="ml-1 text-[10px] bg-muted rounded-full px-1.5 py-0.5">
                  {options.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="notes">
              <Paperclip className="size-3.5" />
              Notes &amp; Docs
            </TabsTrigger>
          </TabsList>

          {/* ── Overview ─────────────────────────────────────────────── */}
          <TabsContent value="overview" className="mt-4 space-y-4">
            <SalesDocumentDetailsCard
              items={[
                { label: 'Created', value: formatDate(template?.createdAt ?? '') },
                { label: 'Updated', value: formatDate(template?.updatedAt ?? '') },
                { label: 'Options', value: options.length },
              ]}
            />
            {options.length > 0 && (
              <DetailCard label={`Options (${options.length})`}>
                <ProposalOptionSummaryList
                  options={options.map((opt, i) => ({
                    key: opt.key,
                    label: opt.label || `Option ${i + 1}`,
                    color: opt.color ?? TIER_CONFIG[i % TIER_CONFIG.length].accent,
                    itemCount: opt.lineItems.length,
                    total: calcProposalTemplateOptionTotal(opt.lineItems),
                  }))}
                />
              </DetailCard>
            )}
          </TabsContent>

          {/* ── Options ──────────────────────────────────────────────── */}
          <TabsContent value="options" className="mt-4 space-y-3">
            {options.length === 0 ? (
              <div className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">
                No options
              </div>
            ) : (
              options.map((opt, idx) => {
                const accent = opt.color ?? TIER_CONFIG[idx % TIER_CONFIG.length].accent;
                const collapsed = viewCollapsed.has(opt.key);
                const optTotal = calcProposalTemplateOptionTotal(opt.lineItems);
                return (
                  <Card key={opt.key} style={{ borderLeftColor: accent, borderLeftWidth: 3 }}>
                    <button
                      type="button"
                      className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left"
                      style={{ backgroundColor: accent + '14' }}
                      onClick={() => toggleViewCollapse(opt.key)}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {collapsed ? (
                          <ChevronDown size={13} className="shrink-0 text-muted-foreground" />
                        ) : (
                          <ChevronUp size={13} className="shrink-0 text-muted-foreground" />
                        )}
                        <span
                          className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: accent }}
                        />
                        <span className="text-sm font-semibold truncate" style={{ color: accent }}>
                          {opt.label || `Option ${idx + 1}`}
                        </span>
                        {opt.description && (
                          <span className="text-xs text-muted-foreground truncate hidden sm:block">
                            — {opt.description}
                          </span>
                        )}
                      </div>
                      <span className="text-sm font-semibold tabular-nums shrink-0">
                        {fmt(optTotal)}
                      </span>
                    </button>
                    {!collapsed && (
                      <>
                        {opt.lineItems.length > 0 ? (
                          <div className="divide-y">
                            {opt.lineItems.map((li) => (
                              <div
                                key={li.lineItemId}
                                className="flex items-center gap-3 px-4 py-2"
                              >
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">{li.name}</p>
                                  <p className="text-xs text-muted-foreground font-mono">
                                    {li.code}
                                  </p>
                                </div>
                                <div className="shrink-0 text-right">
                                  <p className="text-sm font-semibold tabular-nums">
                                    {fmt(calcLineSubtotal(li))}
                                  </p>
                                  <p className="text-[10px] text-muted-foreground tabular-nums">
                                    ×{li.quantity} @ {fmt(li.basePrice)}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="px-4 py-3 text-xs text-muted-foreground">No items</p>
                        )}
                      </>
                    )}
                  </Card>
                );
              })
            )}
          </TabsContent>

          {/* ── Notes ────────────────────────────────────────────────── */}
          <TabsContent value="notes" className="mt-4 space-y-4">
            <SalesDocumentNotesDocsSection
              notes={notes}
              notesLabel="Notes"
              terms={terms.termsAndConditions}
              attachmentsCount={attachments.length}
              renderAttachments={
                <TemplateAttachmentsSection
                  kind="proposal"
                  mode="editable"
                  templateId={id}
                  attachments={attachments}
                  readOnly
                />
              }
              emptyMessage="No notes or terms"
            />
          </TabsContent>
        </Tabs>
      </DetailPageLayout>
    );
  }

  // ── Edit mode ─────────────────────────────────────────────────────────────
  return (
    <FormPageLayout
      title="Edit Proposal Template"
      onBack={discardAndExit}
      noscroll
      actions={
        <>
          <Button variant="outline" size="sm" onClick={discardAndExit}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={updateMutation.isPending}>
            {updateMutation.isPending && <Loader2 size={14} className="mr-1.5 animate-spin" />}
            Save
          </Button>
        </>
      }
    >
      <div className="flex h-full min-h-0">
        <div className="flex-1 overflow-y-auto">
          <div className="space-y-4 px-4 py-4">
            <FormAccordionCard
              open={sec.details}
              header={
                <CollapsibleSectionHeader
                  label="Details"
                  open={sec.details}
                  onToggle={() => toggleSec('details')}
                  badge="required"
                />
              }
              bodyClassName="space-y-4"
            >
              <TemplateDetailsSection
                name={name}
                onNameChange={setName}
                placeholder="Standard Proposal"
                businessUnitIds={businessUnitIds}
                onBusinessUnitIdsChange={setBusinessUnitIds}
              />
            </FormAccordionCard>

            <FormAccordionCard
              open={sec.options}
              header={
                <CollapsibleSectionHeader
                  label="Options"
                  open={sec.options}
                  onToggle={() => toggleSec('options')}
                  badge="required"
                />
              }
              bodyClassName="space-y-4"
            >
              <ProposalTemplateOptionsSection
                options={options}
                onApplySet={(set) => {
                  setOptions(
                    set.map((tier, idx) =>
                      createProposalTemplateOptionDraft({
                        label: tier.label,
                        color: tier.color,
                        collapsed: idx !== 0,
                      }),
                    ),
                  );
                }}
                onAddOption={addOption}
                onUpdateOption={updateOption}
                onRemoveOption={removeOption}
                onUpdateLineItem={updateLineItem}
                onRemoveLineItem={removeLineItem}
                onReorderLineItems={reorderLineItems}
                onReplaceLineItems={replaceLineItems}
                importLabel="Import items from an estimate template"
                importControlFor={(optKey) => (
                  <ProposalTemplateEstimateTemplatePicker
                    onSelect={(template) => importFromEstimateTemplate(optKey, template)}
                    businessUnitId={soleBusinessUnitId(businessUnitIds)}
                  />
                )}
              />
            </FormAccordionCard>

            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Optional
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <FormAccordionCard
              open={sec.notes}
              header={
                <CollapsibleSectionHeader
                  label="Notes &amp; Terms"
                  open={sec.notes}
                  onToggle={() => toggleSec('notes')}
                  badge="optional"
                  statusBadge={
                    description.trim() || notes.trim() || terms.termsMode !== 'DEFAULT'
                      ? 'Added'
                      : undefined
                  }
                />
              }
              bodyClassName="space-y-4"
            >
              <div className="space-y-1">
                <Label className="text-xs">Description</Label>
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Good / Better / Best options"
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Notes</Label>
                <Textarea
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  placeholder="Internal notes visible on the proposal"
                  rows={2}
                  className="text-sm"
                />
              </div>
              <TermsSelector value={terms} onChange={setTerms} />
            </FormAccordionCard>

            <FormAccordionCard
              open={sec.attachments}
              header={
                <CollapsibleSectionHeader
                  label="Attachments"
                  open={sec.attachments}
                  onToggle={() => toggleSec('attachments')}
                  badge="optional"
                  statusBadge={attachments.length > 0 ? 'Added' : undefined}
                />
              }
              bodyClassName="space-y-4"
            >
              <TemplateAttachmentsSection
                kind="proposal"
                mode="editable"
                templateId={id}
                attachments={attachments}
              />
            </FormAccordionCard>
          </div>
        </div>
      </div>
      <TemplateUnsavedChangesDialog
        open={blocker.status === 'blocked'}
        onStay={() => {
          if (blocker.status === 'blocked') blocker.reset();
        }}
        onDiscard={() => {
          if (blocker.status === 'blocked') blocker.proceed();
        }}
      />
    </FormPageLayout>
  );
}
